import { z } from 'zod'
import { PageTypeEnum } from '@/generated/graphql'

// Page type schema
export const pageTypeSchema = z.nativeEnum(PageTypeEnum)
export type PageType = z.infer<typeof pageTypeSchema>

// Block Template schema
export const blockTemplateSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().optional().nullable(),
  type: z.string(),
  tenantType: z.string(),
  tenantId: z.string().optional().nullable(),
  category: z.string().optional().nullable(),
  tags: z.array(z.string()).optional().nullable(),
  defaultProps: z.string().optional().nullable(),
  propsSchema: z.string().optional().nullable(),
  isActive: z.boolean(),
  createdAt: z.any().optional().nullable(),
  updatedAt: z.any().optional().nullable(),
})
export type BlockTemplate = z.infer<typeof blockTemplateSchema>

// Page Content Component schemas
export const contentServiceBlockComponentInputSchema = z.object({
  id: z.string(),
  props: z.string(),
  type: z.string(),
})
export type ContentServiceBlockComponentInput = z.infer<
  typeof contentServiceBlockComponentInputSchema
>

export const contentServiceBlockComponentOutputSchema = z.object({
  id: z.string(),
  props: z.string(),
  type: z.string(),
})
export type ContentServiceBlockComponentOutput = z.infer<
  typeof contentServiceBlockComponentOutputSchema
>

// Page schemas
export const contentServicePageOutputSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: pageTypeSchema,
  description: z.string().optional().nullable(),
  tenantId: z.string().optional().nullable(),
  components: z.array(contentServiceBlockComponentOutputSchema),
  createdAt: z.any().optional().nullable(),
  updatedAt: z.any().optional().nullable(),
})
export type ContentServicePageOutput = z.infer<
  typeof contentServicePageOutputSchema
>

export const contentServicePageInputSchema = z.object({
  id: z.string().optional().nullable(),
  type: pageTypeSchema.optional().nullable(),
})
export type ContentServicePageInput = z.infer<
  typeof contentServicePageInputSchema
>

export const contentServiceCreatePageInputSchema = z.object({
  components: z
    .array(contentServiceBlockComponentInputSchema)
    .optional()
    .nullable(),
  description: z.string().optional().nullable(),
  name: z.string().min(1, 'Name is required'),
  pageRefId: z.string().optional().nullable(),
  slug: z.string().optional().nullable(),
  tenantId: z.string().optional().nullable(),
  type: pageTypeSchema,
})
export type ContentServiceCreatePageInput = z.infer<
  typeof contentServiceCreatePageInputSchema
>

export const contentServiceUpdatePageInputSchema = z.object({
  components: z
    .array(contentServiceBlockComponentInputSchema)
    .optional()
    .nullable(),
  description: z.string().optional().nullable(),
  name: z.string().optional().nullable(),
  pageRefId: z.string().optional().nullable(),
  slug: z.string().optional().nullable(),
  tenantId: z.string().min(1, 'Tenant ID is required'),
  type: pageTypeSchema,
})
export type ContentServiceUpdatePageInput = z.infer<
  typeof contentServiceUpdatePageInputSchema
>

// Query and Mutation Variable schemas
export const getActiveBlockTemplatesQueryVariablesSchema = z.object({})
export type GetActiveBlockTemplatesQueryVariables = z.infer<
  typeof getActiveBlockTemplatesQueryVariablesSchema
>

export const contentServiceGetPageQueryVariablesSchema = z.object({
  id: z.string().optional().nullable(),
  type: pageTypeSchema,
})
export type ContentServiceGetPageQueryVariables = z.infer<
  typeof contentServiceGetPageQueryVariablesSchema
>

export const contentServiceCreatePageMutationVariablesSchema = z.object({
  input: contentServiceCreatePageInputSchema,
})
export type ContentServiceCreatePageMutationVariables = z.infer<
  typeof contentServiceCreatePageMutationVariablesSchema
>

export const contentServiceUpdatePageMutationVariablesSchema = z.object({
  id: z.string().min(1, 'ID is required'),
  input: contentServiceUpdatePageInputSchema,
})
export type ContentServiceUpdatePageMutationVariables = z.infer<
  typeof contentServiceUpdatePageMutationVariablesSchema
>

// Query and Mutation Response schemas
export const getActiveBlockTemplatesQuerySchema = z.object({
  contentServiceBlockTemplates: z.array(blockTemplateSchema),
})
export type GetActiveBlockTemplatesQuery = z.infer<
  typeof getActiveBlockTemplatesQuerySchema
>

export const contentServiceGetPageQuerySchema = z.object({
  contentServiceGetPage: contentServicePageOutputSchema.optional().nullable(),
})
export type ContentServiceGetPageQuery = z.infer<
  typeof contentServiceGetPageQuerySchema
>

export const contentServiceCreatePageMutationSchema = z.object({
  contentServiceCreatePage: contentServicePageOutputSchema,
})
export type ContentServiceCreatePageMutation = z.infer<
  typeof contentServiceCreatePageMutationSchema
>

export const contentServiceUpdatePageMutationSchema = z.object({
  contentServiceUpdatePage: contentServicePageOutputSchema,
})
export type ContentServiceUpdatePageMutation = z.infer<
  typeof contentServiceUpdatePageMutationSchema
>

// Page filter schema
export const pageFilterSchema = z.object({
  type: pageTypeSchema.optional(),
  name: z.string().optional(),
  id: z.string().optional(),
})
export type PageFilter = z.infer<typeof pageFilterSchema>

// Page type options for forms
export const pageTypeOptions = Object.values(PageTypeEnum).map((value) => ({
  label: value,
  value,
}))

export interface PageComponent {
  id: string
  type: string
  props: Record<string, any>
}

export interface DragItem {
  type: 'BLOCK_TEMPLATE' | 'PAGE_COMPONENT'
  id: string
  blockTemplate?: BlockTemplate
  component?: PageComponent
  index?: number
}

export interface PageContent {
  id: string
  name: string
  type: PageTypeEnum
  description?: string
  tenantId?: string
  components: PageComponent[]
  createdAt: string
  updatedAt: string
}
